import * as React from 'react';
import { useRouter } from 'next/router';
import { DEFAULT_VENUE_IMAGE } from 'constants/venues';
import { SportsEnum } from 'types/generated/client';
import { isIndexableVenueForSeo } from 'utils/shared/seo/isIndexableVenueForSeo';
import { YMD_PATTERN } from 'utils/shared/time/zonedIso';
import { useAuthModals } from 'hooks/useAuthModals';
import { useViewer } from 'hooks/useViewer';
import {
  ClubAbout,
  ClubGallery,
  ClubOffers,
  SectionDivider,
} from 'screens/ClubOverview/components';
import Faqs from 'components/Faqs';
import Footer from 'components/Footer';
import { BookCourtPanel } from 'components/courtBooking';
import Head from 'components/utilities/Head';
import classNames from 'styles/utils/classNames';
import {
  BlogSection,
  BreadcrumbsBar,
  ClubAside,
  type ClubTab,
  ClubTabs,
  NearbyCourts,
  UpcomingPrograms,
  VenueCoaches,
  VenueReviews,
  VenueStats,
} from './components';
import { useVenueFollow } from './hooks/useVenueFollow';
import { Props } from './types';
import { generateBreadcrumbs, generateMeta, generateSeoIntro } from './utils';

// followersCount/blogPosts default because ISR pages generated before these props
// existed keep serving their old props until they revalidate.
const SIGNUP_MODAL_TIMEOUT = 5_000;
const IS_SIGNUP_MODAL_OPEN_ENABLED = true;

export default function CourtPageContent({
  venue,
  faqs,
  followersCount = 0,
  blogPosts = [],
}: Props) {
  const { openSignupModal, ModalLogin, ModalSignup } = useAuthModals();
  const { isAnonymousSession } = useViewer();
  const { query } = useRouter();
  // A home-feed offer links here with the day and slot the player tapped.
  const feedDate =
    typeof query.date === 'string' && YMD_PATTERN.test(query.date) ? query.date : undefined;
  const feedSlotId = typeof query.slot === 'string' ? query.slot : undefined;

  // The booking flow signs people in itself, so the ambient nudge stays out of its way.
  const isBookingAuthOpen = React.useRef(false);

  // Nudge anonymous visitors toward signup after a beat; cleared on unmount so
  // court→court navigations don't fire stale timers.
  React.useEffect(() => {
    if (!isAnonymousSession || !IS_SIGNUP_MODAL_OPEN_ENABLED) {
      return;
    }

    const timer = setTimeout(() => {
      if (!isBookingAuthOpen.current) {
        openSignupModal();
      }
    }, SIGNUP_MODAL_TIMEOUT);
    return () => clearTimeout(timer);
  }, [isAnonymousSession]);
  const { isFollowing, isFollowDisabled, toggleFollow, followAsUser } = useVenueFollow({
    venueId: venue?.id,
    onNeedsAuth: openSignupModal,
  });

  // Mobile splits the page into Courts / Events / Club info; desktop shows all
  // three at once, so every tab panel is forced visible from `lg` up.
  const [mobileTab, setMobileTab] = React.useState<ClubTab>('COURTS');
  const showOnTab = (tab: ClubTab) => (mobileTab === tab ? '' : 'max-lg:hidden');

  if (!venue) {
    return null;
  }

  const locationShortName = venue.city?.name
    ? `${venue.city.name}, ${venue.city.countrySubdivision?.name}`
    : '';

  const courtSeoIntro = generateSeoIntro(venue, locationShortName);
  const { metaTitle, metaDescription } = generateMeta({
    venueName: venue.title,
    cityName: locationShortName,
    courtCount: venue.indoorCourtCount + venue.outdoorCourtCount,
    hasTennis: venue.hasTennis,
    hasPadel: venue.hasPadel,
    hasPickleball: venue.hasPickleball,
  });

  return (
    <>
      <Head
        title={metaTitle}
        description={metaDescription}
        ogImage={venue.images[0]?.url || DEFAULT_VENUE_IMAGE}
        canonicalType="currentUrlStripQuery"
        noIndex={!isIndexableVenueForSeo(venue)}
      />
      <div className="fixed -left-40 -top-40 -z-100">
        <p>{courtSeoIntro}</p>
      </div>
      <BreadcrumbsBar breadcrumbs={generateBreadcrumbs(venue)} />
      {/* div, not main — MainLayout (via getLayout) provides the page's <main>. */}
      <div className="container mx-auto w-full max-w-[70rem] pb-20 pt-8 max-sm:px-4">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-[28.5rem_minmax(0,1fr)] lg:gap-x-20">
          <div className="lg:sticky lg:top-20 lg:self-start">
            <ClubAside
              venue={venue}
              isFollowing={isFollowing}
              isFollowDisabled={isFollowDisabled}
              onToggleFollow={toggleFollow}
              tabsSlot={<ClubTabs selectedTab={mobileTab} onTabChange={setMobileTab} />}
              detailsClassName={showOnTab('INFO')}
            />
          </div>

          {/* Sections after the first render their own leading SectionDivider (my-ds-3xl),
              so spacing collapses correctly when a section has no data and hides itself.
              `lg:contents` on the tab wrappers keeps them direct flex children on desktop,
              where there are no tabs and everything stacks as before. */}
          <div className="flex flex-col">
            <BookCourtPanel
              key={venue.id}
              venueId={venue.id}
              venueName={venue.title}
              venueImageUrl={venue.images[0]?.url}
              timezone={venue.timezone}
              initialDate={feedDate}
              initialSlotId={feedSlotId}
              onAuthStepOpen={() => {
                isBookingAuthOpen.current = true;
              }}
              className={classNames('lg:mb-ds-3xl', showOnTab('COURTS'))}
            />

            <div className={classNames('lg:contents', showOnTab('EVENTS'))}>
              <UpcomingPrograms
                venueId={venue.id}
                sports={
                  [
                    venue.hasTennis && SportsEnum.Tennis,
                    venue.hasPickleball && SportsEnum.Pickleball,
                    venue.hasPadel && SportsEnum.Padel,
                  ].filter(Boolean) as SportsEnum[]
                }
              />
            </div>

            <div className={classNames('flex flex-col lg:contents', showOnTab('INFO'))}>
              <VenueCoaches venueId={venue.id} />
              <VenueStats venueId={venue.id} followersCount={followersCount} />
              <div className="mt-ds-3xl flex flex-col gap-10">
                <ClubOffers venue={venue} isShowCourtSetup />
                <ClubAbout description={venue.description || ''} />
                {venue.images.length > 1 && <ClubGallery images={venue.images} />}
              </div>
              <VenueReviews venueId={venue.id} />
              {faqs.length > 0 && (
                <>
                  <SectionDivider className="my-ds-3xl" />
                  <section>
                    <Faqs questionsAnswers={faqs} />
                  </section>
                </>
              )}
            </div>
          </div>
        </div>

        <NearbyCourts venue={venue} />
        <BlogSection posts={blogPosts} />
      </div>
      <Footer isBottomRegister isBottomBarRegisterMobile />
      <ModalLogin isShowSignupLink />
      <ModalSignup
        isShowLoginLink
        handleSignupSuccess={async ({ userId }: { userId: string }) => {
          if (userId && venue.id) {
            followAsUser(userId);
          }
        }}
      />
    </>
  );
}
